import React from 'react';
import { API } from '../api';
import PosterLightbox from './PosterLightbox';

/*
 * What is on at the club, for members.
 *
 * The poster is the point of this screen. A club that has gone to the trouble
 * of making one has already said everything about the event better than a
 * list of fields can, so where a poster exists it leads and the details sit
 * underneath as the readable version — for anyone on a slow connection, using
 * a screen reader, or looking for the date without reading a picture.
 *
 * Posters come from the gallery listing, which carries the 480px thumbnail
 * rather than the full 4K image. A phone showing four events would otherwise
 * download several megabytes to display four pictures a few hundred pixels
 * wide. Tapping one opens it properly.
 */

/* nextDate is the night a repeating event is next on; the API works it out,
   so a Friday night reads as this Friday rather than as the March the
   series started in. */
export const fmtWhen = ev => {
  const start = ev.nextDate || ev.startDate;
  if (!start) return 'Date to be confirmed';
  const d = new Date(String(start).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return 'Date to be confirmed';
  const day = d.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });
  if (ev.repeat || !ev.endDate || String(ev.endDate).slice(0, 10) === String(ev.startDate).slice(0, 10)) return day;
  const e = new Date(String(ev.endDate).slice(0, 10) + 'T00:00:00');
  return day + ' to ' + e.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });
};

function EventsView({ selectedClubId }) {
  const [events, setEvents] = React.useState(null);
  const [posters, setPosters] = React.useState([]);
  const [zoom, setZoom] = React.useState(null);

  React.useEffect(() => {
    /* No club chosen at all. That only happens to a super-admin looking at
       "All Clubs", and it is not the same thing as a club with nothing on —
       rendering the empty state here would say the club has no events when
       nobody has said which club. */
    if (!selectedClubId) { setEvents(undefined); return undefined; }
    let cancelled = false;
    fetch(`${API}/api/events?clubId=${selectedClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setEvents(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setEvents([]); });
    /* gallery=1 leaves the full images behind and brings the thumbnails. */
    fetch(`${API}/api/posters?clubId=${selectedClubId}&gallery=1`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPosters(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedClubId]);

  /* A poster earns its place on this screen only if it is switched on for the
     club screen and has not passed its show-until date — the same test the
     television uses. An admin should not have to remember two answers to the
     question "is this poster live?". */
  const posterFor = React.useCallback(ev => {
    const today = new Date().toISOString().slice(0, 10);
    return posters.find(p => String(p.EventId) === String(ev.id)
      && p.active
      && (!p.showUntil || String(p.showUntil).slice(0, 10) >= today)
      && p.thumbData) || null;
  }, [posters]);

  if (events === undefined) {
    return (
      <div className="content-medium" style={{ paddingTop: 'var(--gs-space-6)' }}>
        <div className="gs-panel" style={{ textAlign: 'center', padding: 'var(--gs-space-8) var(--gs-space-4)' }}>
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
            Choose a club to see what is on.
          </p>
        </div>
      </div>
    );
  }

  if (events === null) {
    return (
      <div className="content-medium" style={{ paddingTop: 'var(--gs-space-6)' }}>
        <p style={{ color: 'var(--gs-text-muted)' }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content-medium" style={{ paddingTop: 'var(--gs-space-4)' }}>
      <h2 style={{ margin: '0 0 var(--gs-space-1)', fontSize: 'var(--gs-text-xl)' }}>What&rsquo;s on</h2>
      <p style={{ marginTop: 0, color: 'var(--gs-text-muted)' }}>
        Everything at the club that is not a competition.
      </p>

      {events.length === 0 ? (
        <div className="gs-panel" style={{ textAlign: 'center', padding: 'var(--gs-space-8) var(--gs-space-4)' }}>
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
            Nothing on at the moment. Anything the club puts on will show up here.
          </p>
        </div>
      ) : events.map(ev => {
        const poster = posterFor(ev);
        const facts = [ev.startTime, ev.cost, ev.location].map(x => (x || '').trim()).filter(Boolean);
        return (
          <article key={ev.id} className="ev-card">
            {poster && (
              <button
                type="button"
                className="ev-poster"
                onClick={() => setZoom(poster)}
                aria-label={`See the poster for ${ev.name}`}>
                <img src={poster.thumbData} alt="" />
              </button>
            )}
            <div className="ev-body">
              <h3 className="ev-name">{ev.name}</h3>
              {ev.kind && <div className="ev-kind">{ev.kind}</div>}
              <div className="ev-when">{fmtWhen(ev)}</div>
              {facts.length > 0 && <div className="ev-facts">{facts.join(' · ')}</div>}
              {ev.details && <p className="ev-details">{ev.details}</p>}
            </div>
          </article>
        );
      })}

      {zoom && <PosterLightbox poster={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}

export default EventsView;
